import { parseUTCFromAPIToUnix } from '@/composable/useFormatDateAndHours'
import { SensorType } from '@/interfaces/enums'
import { i18n } from '@/plugins/i18n'
import { extractDoorOpenPeriods } from '@/utils/dashboard/door-correlation-background'
import {
  buildRegisterChartSeries,
  type IRegisterChartSensorData,
} from '@/utils/components/register-chart'

/** Mapeia chaves legadas da API / Influx para a chave canônica usada nas traduções. */
const LEGACY_TO_CANONICAL: Record<string, string> = {
  Temperatura: 'temperature',
  AVERAGE: 'average_current',
  average: 'average_current',
}

/** Série contínua (energia ou temperatura) pronta para `buildChartOption`. */
export interface RegisterChartContinuousInput {
  name: string
  unit: string
  data: Array<{ x: number; y: number }>
}

/** Swimlane de porta: nome do lane + intervalos abertos em unix ms. */
export interface RegisterChartDoorLaneInput {
  name: string
  intervals: Array<{ start: number; end: number }>
}

export interface RegisterChartSensorPartition {
  energy: IRegisterChartSensorData[]
  temperature: IRegisterChartSensorData[]
  doors: IRegisterChartSensorData[]
}

export function canonicalRegisterSeriesKey(rawKey: string): string {
  return LEGACY_TO_CANONICAL[rawKey] ?? rawKey
}

/** Rótulo para legenda/tooltip do gráfico de registros (vue-i18n). */
export function translateRegisterChartSeriesName(rawKey: string): string {
  const canonical = canonicalRegisterSeriesKey(rawKey)
  const path = `registers.chart.series.${canonical}`
  if (i18n.global.te(path)) {
    return String(i18n.global.t(path))
  }
  return rawKey
}

function unitLooksLikeTemperature(unit: string | undefined): boolean {
  return unit != null && (unit.includes('°C') || unit.includes('ºC'))
}

/** Particiona sensores em energia / temperatura / portas para o layout multi-grid. */
export function partitionRegisterSensors(
  sensors: Array<IRegisterChartSensorData | null | undefined>,
): RegisterChartSensorPartition {
  const partition: RegisterChartSensorPartition = { energy: [], temperature: [], doors: [] }

  for (const sensorData of sensors) {
    if (!sensorData) continue
    const sensorType = sensorData.sensor?.sensor_type

    if (sensorType === SensorType.DOOR_OPEN) {
      partition.doors.push(sensorData)
      continue
    }
    if (
      sensorType === SensorType.TEMPERATURE ||
      (sensorType == null && unitLooksLikeTemperature(sensorData.sensor?.unit_display))
    ) {
      partition.temperature.push(sensorData)
      continue
    }
    partition.energy.push(sensorData)
  }

  return partition
}

/** Converte readings em entradas contínuas (timestamps unix ms) para o ECharts. */
export function buildRegisterChartContinuousInputs(
  sensorData: IRegisterChartSensorData | null | undefined,
  options: { namePrefix?: string; excludeDoorChannels?: boolean } = {},
): RegisterChartContinuousInput[] {
  if (!sensorData) return []

  if (options.excludeDoorChannels && sensorData.sensor?.sensor_type === SensorType.DOOR_OPEN) {
    return []
  }

  const namePrefix = options.namePrefix?.trim() || undefined
  return buildRegisterChartSeries(sensorData, namePrefix ? { namePrefix } : {}).map((series) => ({
    name: series.name,
    unit: series.unitDisplay ?? sensorData.sensor?.unit_display ?? '',
    data: (series.data ?? []).map((point) => ({
      x: point.x instanceof Date ? point.x.getTime() : new Date(point.x).getTime(),
      y: point.y,
    })),
  }))
}

/** Monta os swimlanes de porta (intervalos abertos) a partir dos payloads door_open. */
export function buildRegisterChartDoorLanes(
  doorSensors: Array<IRegisterChartSensorData | null | undefined>,
  range: { min?: number; max?: number },
): RegisterChartDoorLaneInput[] {
  const rangeEnd = range.max ?? Date.now()

  return doorSensors.flatMap((sensorData, index) => {
    if (!sensorData) return []

    const fallbackName = String(i18n.global.t('registers.chart.door_lane', { index: index + 1 }))
    const name = sensorData.sensor?.name?.trim() || fallbackName

    const periods = extractDoorOpenPeriods(sensorData.readings, rangeEnd)
    const intervals = periods
      .map((period) => {
        const start = parseUTCFromAPIToUnix(period.start_date)
        const end = period.end_date != null ? parseUTCFromAPIToUnix(period.end_date) : rangeEnd
        return { start, end }
      })
      .filter((interval) => Number.isFinite(interval.start) && Number.isFinite(interval.end) && interval.end > interval.start)

    return [{ name, intervals }]
  })
}
